export const featureAdminDe = {
  title: 'Funktionen & Module',
  subtitle: 'Mandantenweite Verfügbarkeit, Abhängigkeiten und sichere Stilllegung verwalten.',
  search: 'Funktionen durchsuchen',
  searchPlaceholder: 'Name, Beschreibung oder technischer Schlüssel',
  recommended: 'Empfohlene Standardmodule aktivieren',
  recommendedSuccess: 'Alle aktuell verfügbaren Standardmodule wurden aktiviert.',
  empty: 'Keine passende Funktion gefunden.',
  loadError: 'Der Funktionskatalog konnte nicht geladen werden.',
  active: 'Aktiv',
  inactive: 'Deaktiviert',
  desiredPending: 'Gewünschter Status ist noch nicht wirksam',
  systemCore: 'Systemkern – immer aktiv',
  missingConfiguration: 'Konfiguration fehlt',
  configurationAlternative: ' oder ',
  configurationRequirements: {
    'tenant:mail_accounts': 'Ein aktives geschäftliches Postfach mit hinterlegten Zugangsdaten.',
    OLLAMA_ENABLED:
      'Ollama ist nicht aktiviert. In der Serverkonfiguration OLLAMA_ENABLED=true setzen.',
    OLLAMA_API_URL:
      'Die Ollama-Serveradresse fehlt oder ist ungültig. OLLAMA_API_URL in der Serverkonfiguration hinterlegen.',
    OLLAMA_MODEL:
      'Das Ollama-Modell fehlt oder ist ungültig. OLLAMA_MODEL in der Serverkonfiguration hinterlegen.',
  },
  missingDependency: 'Abhängigkeit fehlt',
  platformUnavailable: 'Plattform nicht verfügbar',
  configure: 'Konfiguration öffnen',
  dependencies: 'Benötigt',
  dependents: 'Betroffene Unterfunktionen',
  none: 'Keine',
  records: '{{count}} Datensätze',
  jobs: '{{count}} laufende Jobs',
  pausedJobs: '{{count}} pausierte Jobs',
  openTasks: '{{count}} offene Vorgänge',
  resource: 'Ressourcen',
  enableLabel: '{{name}} aktivieren',
  disableLabel: '{{name}} deaktivieren',
  impact: {
    enableTitle: '{{name}} aktivieren?',
    disableTitle: '{{name}} sicher deaktivieren?',
    description:
      'Prüfen Sie die ermittelten Folgen. Daten werden nicht gelöscht und historische Nachweise bleiben erhalten.',
    affected: 'Gemeinsam betroffene Funktionen',
    activeJobs: 'Aktive Hintergrundaufträge',
    campaigns: 'Geplante oder laufende Kampagnen',
    openRecords: 'Offene Vorgänge',
    endpoints: 'Öffentliche Endpunkte',
    webhooks: 'Webhooks',
    portalUsers: 'Betroffene Portalbenutzer',
    dataRecords: 'Relevante Datenbestände',
    consequences: 'Folgen der Deaktivierung',
    reactivation: 'Wiederaktivierung',
    blockers: 'Aktivierung derzeit blockiert',
    reason: 'Begründung',
    reasonRequired: 'Für dieses kritische Modul ist eine Begründung erforderlich.',
    reasonPlaceholder: 'Nachvollziehbarer betrieblicher Grund',
    confirmation: 'Ich habe Abhängigkeiten und Folgen geprüft und bestätige diesen Plan.',
    enable: 'Aktivierung bestätigen',
    disable: 'Deaktivierung bestätigen',
    successEnabled: '{{name}} wurde aktiviert.',
    successDisabled: '{{name}} wurde sicher deaktiviert.',
  },
  blockers: {
    CONFIGURATION_MISSING: 'Die notwendige Konfiguration ist unvollständig.',
    DEPENDENCY_UNAVAILABLE: 'Mindestens eine Abhängigkeit ist nicht verfügbar.',
    PLATFORM_UNAVAILABLE: 'Die Funktion ist auf dieser Plattform nicht verfügbar.',
    PLAN_UNAVAILABLE: 'Die Plattformberechtigung erlaubt diese Funktion nicht.',
    DEPENDENT_FEATURES_REQUIRE_CASCADE:
      'Abhängige Funktionen werden im bestätigten Plan gemeinsam deaktiviert.',
    ACTIVE_JOBS_REQUIRE_SAFE_PAUSE: 'Aktive Jobs werden kontrolliert pausiert oder beendet.',
    OPEN_RECORDS_REMAIN_READ_ONLY: 'Offene Vorgänge bleiben als historische Lesedaten erhalten.',
  },
  history: {
    title: 'Änderungsvergangenheit',
    subtitle: 'Vollständige auditierte Statusänderungen dieses Mandanten.',
    empty: 'Es liegen noch keine Feature-Änderungen vor.',
    enabled: 'aktiviert',
    disabled: 'deaktiviert',
    by: 'durch {{name}}',
    system: 'Systemprozess',
    previous: 'Zurück',
    next: 'Weiter',
    page: 'Seite {{page}} von {{pages}}',
  },
  categories: {
    system: 'Systemkern',
    sales: 'Vertrieb & CRM',
    catalogue: 'Artikel & Katalog',
    operations: 'Betrieb & Dokumente',
    finance: 'Finanzen',
    communication: 'Kommunikation',
    'product-development': 'Produktentwicklung',
    manufacturing: 'Produktion',
    analytics: 'Analysen',
  },
} as const;

export const featureAdminEn = {
  title: 'Features & modules',
  subtitle: 'Manage tenant-wide availability, dependencies, and safe shutdown.',
  search: 'Search features',
  searchPlaceholder: 'Name, description, or technical key',
  recommended: 'Enable recommended standard modules',
  recommendedSuccess: 'All currently available standard modules were enabled.',
  empty: 'No matching feature found.',
  loadError: 'The feature catalogue could not be loaded.',
  active: 'Active',
  inactive: 'Disabled',
  desiredPending: 'The requested state is not effective yet',
  systemCore: 'System core – always active',
  missingConfiguration: 'Configuration missing',
  configurationAlternative: ' or ',
  configurationRequirements: {
    'tenant:mail_accounts': 'An active business mailbox with stored credentials.',
    OLLAMA_ENABLED: 'Ollama is not enabled. Set OLLAMA_ENABLED=true in the server configuration.',
    OLLAMA_API_URL:
      'The Ollama server URL is missing or invalid. Set OLLAMA_API_URL in the server configuration.',
    OLLAMA_MODEL:
      'The Ollama model is missing or invalid. Set OLLAMA_MODEL in the server configuration.',
  },
  missingDependency: 'Dependency missing',
  platformUnavailable: 'Platform unavailable',
  configure: 'Open configuration',
  dependencies: 'Requires',
  dependents: 'Affected subfeatures',
  none: 'None',
  records: '{{count}} records',
  jobs: '{{count}} active jobs',
  pausedJobs: '{{count}} paused jobs',
  openTasks: '{{count}} open items',
  resource: 'Resources',
  enableLabel: 'Enable {{name}}',
  disableLabel: 'Disable {{name}}',
  impact: {
    enableTitle: 'Enable {{name}}?',
    disableTitle: 'Safely disable {{name}}?',
    description:
      'Review the calculated impact. No data is deleted and historical evidence remains available.',
    affected: 'Features changed together',
    activeJobs: 'Active background jobs',
    campaigns: 'Scheduled or running campaigns',
    openRecords: 'Open business items',
    endpoints: 'Public endpoints',
    webhooks: 'Webhooks',
    portalUsers: 'Affected portal users',
    dataRecords: 'Relevant data records',
    consequences: 'Shutdown consequences',
    reactivation: 'Reactivation',
    blockers: 'Activation is currently blocked',
    reason: 'Reason',
    reasonRequired: 'A reason is required for this critical module.',
    reasonPlaceholder: 'Traceable operational reason',
    confirmation: 'I reviewed the dependencies and impact and confirm this plan.',
    enable: 'Confirm activation',
    disable: 'Confirm shutdown',
    successEnabled: '{{name}} was enabled.',
    successDisabled: '{{name}} was safely disabled.',
  },
  blockers: {
    CONFIGURATION_MISSING: 'Required configuration is incomplete.',
    DEPENDENCY_UNAVAILABLE: 'At least one dependency is unavailable.',
    PLATFORM_UNAVAILABLE: 'This feature is unavailable on the platform.',
    PLAN_UNAVAILABLE: 'The platform entitlement does not permit this feature.',
    DEPENDENT_FEATURES_REQUIRE_CASCADE:
      'Dependent features are disabled together in the confirmed plan.',
    ACTIVE_JOBS_REQUIRE_SAFE_PAUSE: 'Active jobs are paused or completed in a controlled way.',
    OPEN_RECORDS_REMAIN_READ_ONLY: 'Open items remain available as historical read-only data.',
  },
  history: {
    title: 'Change history',
    subtitle: 'Complete audited status changes for this tenant.',
    empty: 'No feature changes have been recorded yet.',
    enabled: 'enabled',
    disabled: 'disabled',
    by: 'by {{name}}',
    system: 'System process',
    previous: 'Previous',
    next: 'Next',
    page: 'Page {{page}} of {{pages}}',
  },
  categories: {
    system: 'System core',
    sales: 'Sales & CRM',
    catalogue: 'Products & catalogue',
    operations: 'Operations & documents',
    finance: 'Finance',
    communication: 'Communication',
    'product-development': 'Product development',
    manufacturing: 'Manufacturing',
    analytics: 'Analytics',
  },
} as const;

export const featureAdminTr = {
  ...featureAdminEn,
  configurationRequirements: {
    ...featureAdminEn.configurationRequirements,
    'tenant:mail_accounts': 'Kayıtlı kimlik bilgileri olan etkin bir iş posta kutusu.',
  },
  blockers: {
    ...featureAdminEn.blockers,
    CONFIGURATION_MISSING: 'Gerekli yapılandırma eksik.',
    DEPENDENCY_UNAVAILABLE: 'En az bir bağımlılık kullanılamıyor.',
  },
  title: 'Özellikler ve modüller',
  subtitle: 'Kiracı genelindeki kullanılabilirliği, bağımlılıkları ve güvenli kapatmayı yönetin.',
  search: 'Özellik ara',
  searchPlaceholder: 'Ad, açıklama veya teknik anahtar',
  recommended: 'Önerilen standart modülleri etkinleştir',
  recommendedSuccess: 'Kullanılabilir tüm standart modüller etkinleştirildi.',
  empty: 'Eşleşen özellik bulunamadı.',
  loadError: 'Özellik kataloğu yüklenemedi.',
  active: 'Etkin',
  inactive: 'Devre dışı',
  desiredPending: 'İstenen durum henüz yürürlükte değil',
  systemCore: 'Sistem çekirdeği – her zaman etkin',
  missingConfiguration: 'Yapılandırma eksik',
  missingDependency: 'Bağımlılık eksik',
  platformUnavailable: 'Platformda kullanılamıyor',
  configure: 'Yapılandırmayı aç',
  dependencies: 'Gereksinimler',
  dependents: 'Etkilenen alt özellikler',
  records: '{{count}} kayıt',
  jobs: '{{count}} etkin iş',
  pausedJobs: '{{count}} duraklatılan iş',
  openTasks: '{{count}} açık işlem',
  enableLabel: '{{name}} özelliğini etkinleştir',
  disableLabel: '{{name}} özelliğini devre dışı bırak',
  impact: {
    ...featureAdminEn.impact,
    enableTitle: '{{name}} etkinleştirilsin mi?',
    disableTitle: '{{name}} güvenle devre dışı bırakılsın mı?',
    description: 'Hesaplanan etkiyi inceleyin. Hiçbir veri silinmez ve geçmiş kanıtlar korunur.',
    affected: 'Birlikte değiştirilen özellikler',
    activeJobs: 'Etkin arka plan işleri',
    campaigns: 'Planlanmış veya çalışan kampanyalar',
    openRecords: 'Açık işlemler',
    endpoints: 'Genel uç noktalar',
    portalUsers: 'Etkilenen portal kullanıcıları',
    dataRecords: 'İlgili veri kayıtları',
    consequences: 'Kapatmanın sonuçları',
    reactivation: 'Yeniden etkinleştirme',
    blockers: 'Etkinleştirme şu anda engellendi',
    reason: 'Gerekçe',
    reasonRequired: 'Bu kritik modül için gerekçe zorunludur.',
    reasonPlaceholder: 'İzlenebilir operasyonel gerekçe',
    confirmation: 'Bağımlılıkları ve etkileri inceledim ve bu planı onaylıyorum.',
    enable: 'Etkinleştirmeyi onayla',
    disable: 'Kapatmayı onayla',
    successEnabled: '{{name}} etkinleştirildi.',
    successDisabled: '{{name}} güvenle devre dışı bırakıldı.',
  },
  history: {
    ...featureAdminEn.history,
    title: 'Değişiklik geçmişi',
    subtitle: 'Bu kiracının denetlenmiş tüm durum değişiklikleri.',
    empty: 'Henüz özellik değişikliği kaydedilmedi.',
    enabled: 'etkinleştirildi',
    disabled: 'devre dışı bırakıldı',
    by: '{{name}} tarafından',
    system: 'Sistem işlemi',
    previous: 'Önceki',
    next: 'Sonraki',
    page: 'Sayfa {{page}} / {{pages}}',
  },
  categories: {
    system: 'Sistem çekirdeği',
    sales: 'Satış ve CRM',
    catalogue: 'Ürünler ve katalog',
    operations: 'Operasyonlar ve belgeler',
    finance: 'Finans',
    communication: 'İletişim',
    'product-development': 'Ürün geliştirme',
    manufacturing: 'Üretim',
    analytics: 'Analizler',
  },
} as const;

export const featureAdminFr = {
  ...featureAdminEn,
  configurationRequirements: {
    ...featureAdminEn.configurationRequirements,
    'tenant:mail_accounts': 'Une boîte professionnelle active avec des identifiants enregistrés.',
  },
  blockers: {
    ...featureAdminEn.blockers,
    CONFIGURATION_MISSING: 'La configuration nécessaire est incomplète.',
    DEPENDENCY_UNAVAILABLE: 'Au moins une dépendance est indisponible.',
  },
  title: 'Fonctions et modules',
  subtitle: 'Gérer la disponibilité, les dépendances et l’arrêt sûr pour le tenant.',
  search: 'Rechercher des fonctions',
  searchPlaceholder: 'Nom, description ou clé technique',
  recommended: 'Activer les modules standard recommandés',
  recommendedSuccess: 'Tous les modules standard actuellement disponibles ont été activés.',
  empty: 'Aucune fonction correspondante.',
  loadError: 'Le catalogue des fonctions n’a pas pu être chargé.',
  active: 'Actif',
  inactive: 'Désactivé',
  desiredPending: 'L’état demandé n’est pas encore effectif',
  systemCore: 'Noyau système – toujours actif',
  missingConfiguration: 'Configuration manquante',
  missingDependency: 'Dépendance manquante',
  platformUnavailable: 'Plateforme indisponible',
  configure: 'Ouvrir la configuration',
  dependencies: 'Nécessite',
  dependents: 'Sous-fonctions concernées',
  records: '{{count}} enregistrements',
  jobs: '{{count}} tâches actives',
  pausedJobs: '{{count}} tâches en pause',
  openTasks: '{{count}} éléments ouverts',
  enableLabel: 'Activer {{name}}',
  disableLabel: 'Désactiver {{name}}',
  impact: {
    ...featureAdminEn.impact,
    enableTitle: 'Activer {{name}} ?',
    disableTitle: 'Désactiver {{name}} en toute sécurité ?',
    description:
      'Vérifiez l’impact calculé. Aucune donnée n’est supprimée et les preuves sont conservées.',
    affected: 'Fonctions modifiées ensemble',
    activeJobs: 'Tâches d’arrière-plan actives',
    campaigns: 'Campagnes planifiées ou en cours',
    openRecords: 'Éléments métier ouverts',
    endpoints: 'Points d’accès publics',
    portalUsers: 'Utilisateurs du portail concernés',
    dataRecords: 'Données pertinentes',
    consequences: 'Conséquences de l’arrêt',
    reactivation: 'Réactivation',
    blockers: 'Activation actuellement bloquée',
    reason: 'Motif',
    reasonRequired: 'Un motif est requis pour ce module critique.',
    reasonPlaceholder: 'Motif opérationnel traçable',
    confirmation: 'J’ai vérifié les dépendances et les conséquences et je confirme ce plan.',
    enable: 'Confirmer l’activation',
    disable: 'Confirmer l’arrêt',
    successEnabled: '{{name}} a été activé.',
    successDisabled: '{{name}} a été désactivé en toute sécurité.',
  },
  history: {
    ...featureAdminEn.history,
    title: 'Historique des modifications',
    subtitle: 'Toutes les modifications de statut auditées de ce tenant.',
    empty: 'Aucune modification de fonction enregistrée.',
    enabled: 'activé',
    disabled: 'désactivé',
    by: 'par {{name}}',
    system: 'Processus système',
    previous: 'Précédent',
    next: 'Suivant',
    page: 'Page {{page}} sur {{pages}}',
  },
  categories: {
    system: 'Noyau système',
    sales: 'Ventes et CRM',
    catalogue: 'Articles et catalogue',
    operations: 'Opérations et documents',
    finance: 'Finance',
    communication: 'Communication',
    'product-development': 'Développement produit',
    manufacturing: 'Production',
    analytics: 'Analyses',
  },
} as const;

export const featureAdminEs = {
  ...featureAdminEn,
  configurationRequirements: {
    ...featureAdminEn.configurationRequirements,
    'tenant:mail_accounts': 'Un buzón profesional activo con credenciales guardadas.',
  },
  blockers: {
    ...featureAdminEn.blockers,
    CONFIGURATION_MISSING: 'La configuración necesaria está incompleta.',
    DEPENDENCY_UNAVAILABLE: 'Al menos una dependencia no está disponible.',
  },
  title: 'Funciones y módulos',
  subtitle: 'Gestiona disponibilidad, dependencias y desactivación segura para el tenant.',
  search: 'Buscar funciones',
  searchPlaceholder: 'Nombre, descripción o clave técnica',
  recommended: 'Activar módulos estándar recomendados',
  recommendedSuccess: 'Se activaron todos los módulos estándar disponibles.',
  empty: 'No se encontró ninguna función.',
  loadError: 'No se pudo cargar el catálogo de funciones.',
  active: 'Activo',
  inactive: 'Desactivado',
  desiredPending: 'El estado solicitado aún no es efectivo',
  systemCore: 'Núcleo del sistema – siempre activo',
  missingConfiguration: 'Falta configuración',
  missingDependency: 'Falta una dependencia',
  platformUnavailable: 'No disponible en la plataforma',
  configure: 'Abrir configuración',
  dependencies: 'Requiere',
  dependents: 'Subfunciones afectadas',
  records: '{{count}} registros',
  jobs: '{{count}} trabajos activos',
  pausedJobs: '{{count}} trabajos pausados',
  openTasks: '{{count}} elementos abiertos',
  enableLabel: 'Activar {{name}}',
  disableLabel: 'Desactivar {{name}}',
  impact: {
    ...featureAdminEn.impact,
    enableTitle: '¿Activar {{name}}?',
    disableTitle: '¿Desactivar {{name}} de forma segura?',
    description:
      'Revise el impacto calculado. No se eliminan datos y se conservan las pruebas históricas.',
    affected: 'Funciones modificadas juntas',
    activeJobs: 'Trabajos en segundo plano activos',
    campaigns: 'Campañas programadas o en curso',
    openRecords: 'Elementos de negocio abiertos',
    endpoints: 'Endpoints públicos',
    portalUsers: 'Usuarios del portal afectados',
    dataRecords: 'Datos relevantes',
    consequences: 'Consecuencias de la desactivación',
    reactivation: 'Reactivación',
    blockers: 'La activación está bloqueada',
    reason: 'Motivo',
    reasonRequired: 'Este módulo crítico requiere un motivo.',
    reasonPlaceholder: 'Motivo operativo trazable',
    confirmation: 'He revisado las dependencias y el impacto y confirmo este plan.',
    enable: 'Confirmar activación',
    disable: 'Confirmar desactivación',
    successEnabled: 'Se activó {{name}}.',
    successDisabled: 'Se desactivó {{name}} de forma segura.',
  },
  history: {
    ...featureAdminEn.history,
    title: 'Historial de cambios',
    subtitle: 'Todos los cambios de estado auditados de este tenant.',
    empty: 'Aún no hay cambios de funciones.',
    enabled: 'activó',
    disabled: 'desactivó',
    by: 'por {{name}}',
    system: 'Proceso del sistema',
    previous: 'Anterior',
    next: 'Siguiente',
    page: 'Página {{page}} de {{pages}}',
  },
  categories: {
    system: 'Núcleo del sistema',
    sales: 'Ventas y CRM',
    catalogue: 'Productos y catálogo',
    operations: 'Operaciones y documentos',
    finance: 'Finanzas',
    communication: 'Comunicación',
    'product-development': 'Desarrollo de producto',
    manufacturing: 'Producción',
    analytics: 'Análisis',
  },
} as const;
